import React from 'react';
import styled from 'styled-components';

import { COLORS } from '@cuemath/comps';
import { Text } from '@cuemath/web-comps';
import { Close } from 'styled-icons/material/Close';

import { useAppContext } from 'app-context';
import CoursesInfo from 'modules/drawers/courses-info';
import DetailedPocs from 'modules/drawers/detailed-pocs';
import MoreIds from 'modules/drawers/more-ids';
import ParentInfo from 'modules/drawers/parent-info';
import PaymentInfo from 'modules/drawers/payment-info';
import SessionInfo from 'modules/drawers/session-info';
import ReferralIncentives from 'modules/drawers/referral-incentives';
import ReferralQualifyingCriteria from 'modules/drawers/referral-qualifying-criteria';
import ReferralQualifyingCriteria2 from 'modules/drawers/referral-qualifying-criteria-2';
import ReferralTemplate from 'modules/drawers/referral-template';
import ReferralTrackedEvents from 'modules/drawers/referral-tracked-events';
import ReferralTrackedProgressEvents from 'modules/drawers/referral-tracked-progress-events';
import ReferralValidityCriteria from 'modules/drawers/referral-validiity-criteria';
import ReferralWeightedExperiments from 'modules/drawers/referral-weighted-experiments';
import TeacherLicense from 'modules/drawers/teacher-license';
import TrialPreference from 'modules/drawers/trial-preferences';
import StudentTransferSlotPending from 'modules/drawers/student-transfer/slots-pending';
import TeacherNotFoundDetails from 'modules/drawers/student-transfer/teacher-not-found';
import SearchingTeacherDetails from 'modules/drawers/student-transfer/searching-teacher';
import ClassBookedDetails from 'modules/drawers/student-transfer/class-booked';
import STParentUnhappy from 'modules/drawers/student-transfer/parent-unhappy';
import PauseHistoryInfo from 'modules/drawers/pause-history-info';
import MakeUpClassesInfo from 'modules/drawers/make-up-classes-info';

import { TEACHER_DETAILS_DRAWERS } from 'constants/teacher-details';
import STTransferSuccessful from '../../modules/drawers/student-transfer/transfer-successful';
import EditStudentTransferSlots from '../../modules/drawers/student-transfer/edit-slot';
import { PAUSE_HISTORY_INFO_DRAWER, MAKE_UP_CLASSES_INFO_DRAWER } from 'constants/pause';

const DrawerContainer = styled.div`
  height: 100%;
  background: ${COLORS.WHITE};
  box-shadow: 1px 0px 7px rgba(0, 0, 0, 0.5);
  position: fixed;
  top: 0;
  right: 0;
  width: 70%;
  max-width: 352px;
  z-index: 200;
  transform: ${props => (props.isOpen ? 'translateX(0%)' : 'translateX(100%)')};
  transition: transform 0.3s ease-out;
  @media (max-width: 767px) {
    transform: ${props => (props.isOpen ? 'translateY(20%)' : 'translateY(100%)')};
    width: 100%;
    max-width: 100%;
    bottom: 0;
    left: 0;
    border-top-right-radius: 10px;
    border-top-left-radius: 10px;
  }
`;

const Heading = styled.div`
  background: ${COLORS.COLA};
  height: 56px;
  display: flex;
  justify-content: space-between;
  padding: 16px;
`;

const CloseButton = styled(Close)`
  cursor: pointer;
`;

const Container = styled.div`
  padding: 16px;
  max-height: calc(100vh - 56px);
  overflow-y: auto;
`;

const Drawer = () => {
  const {
    sidePanelView: { view, title, props },
    setSidePanelView,
  } = useAppContext();

  function close() {
    setSidePanelView({ view: '', props: {}, title: '' });
  }

  function getView() {
    const DRAWER_COMP_MAP = {
      'course-info': <CoursesInfo {...props} />,
      'detailed-pocs': <DetailedPocs {...props} />,
      'more-ids': <MoreIds {...props} />,
      'notes': <h1>Notes</h1>,
      'parent-info': <ParentInfo {...props} />,
      'payment-info': <PaymentInfo {...props} />,
      'session-info': <SessionInfo {...props} close={close} />,
      'referral-incentives': <ReferralIncentives incentives={props} />,
      'referral-qualifying-criteria': (
        <ReferralQualifyingCriteria qualifyingCriteria={props} />
      ),
      'referral-qualifying-criteria-2': (
        <ReferralQualifyingCriteria2 qualifyingCriteria={props} />
      ),
      'referral-template': <ReferralTemplate template={props} />,
      'referral-tracked-events': <ReferralTrackedEvents trackedEvents={props} />,
      'referral-tracked-progress-events': (
        <ReferralTrackedProgressEvents trackedProgressEvents={props} />
      ),
      'referral-validity-criteria': <ReferralValidityCriteria validityCriteria={props} />,
      'referral-weighted-experiments': (
        <ReferralWeightedExperiments weightedExperiments={props} />
      ),
      'student-transfer-slot-pending': <StudentTransferSlotPending {...props} />,
      'st-teacher-not-found': <TeacherNotFoundDetails data={props} />,
      'st-searching-teacher': <SearchingTeacherDetails data={props} />,
      'st-class-booked': <ClassBookedDetails data={props} />,
      'st-parent-unhappy': <STParentUnhappy data={props} />,
      'st-transfer-successful': <STTransferSuccessful data={props} />,
      'st-edit-slots': <EditStudentTransferSlots data={props} />,
      [TEACHER_DETAILS_DRAWERS.LICENSE]: <TeacherLicense />,
      [PAUSE_HISTORY_INFO_DRAWER]: <PauseHistoryInfo {...props} />,
      [MAKE_UP_CLASSES_INFO_DRAWER]: <MakeUpClassesInfo {...props} />,
      [TEACHER_DETAILS_DRAWERS.TRIAL_PREFERENCE]: <TrialPreference />,
    };

    return DRAWER_COMP_MAP[view];
  }

  return (
    <DrawerContainer isOpen={Boolean(view)}>
      <Heading>
        <Text variant="h6" color={COLORS.WHITE}>
          {title}
        </Text>
        <CloseButton color="white" onClick={close} />
      </Heading>
      <Container>{getView()}</Container>
    </DrawerContainer>
  );
};

export default Drawer;
